/**
 * Zentrales Notification-Routing.
 *
 * Löst eine Notification in ein Navigationsziel auf und stellt einen
 * robusten Klick-Handler bereit. Verhindert White-Pages bei alten oder
 * kaputten Benachrichtigungen und reicht `fromNotification`-State durch,
 * damit Zielseiten (z.B. SessionDetailPage) erkennen können, dass der
 * User aus der Benachrichtigungs-History kommt.
 */

import { toast } from '../components/notifications';

const sessionTarget = (notification) => {
  const sessionId = notification?.data?.sessionId;
  if (!sessionId) return null;
  return {
    path: `/session/${sessionId}`,
    state: {
      fromNotification: true,
      notificationCreatedAt: notification.createdAt || null,
      notificationType: notification.type,
    },
  };
};

const chatTarget = (notification) => {
  const chatId = notification?.data?.chatId;
  if (!chatId) return null;
  return { path: `/app?view=chat&chatId=${chatId}`, state: null };
};

export const resolveNotificationTarget = (notification) => {
  if (!notification || !notification.type) return null;

  const actionUrl = notification.data?.actionUrl;

  switch (notification.type) {
    case 'CHAT_MESSAGE':
      return chatTarget(notification);

    case 'CHAT_REQUEST':
      return { path: '/app?view=chat-overview', state: null };

    case 'CONNECTION_ACCEPTED':
      return chatTarget(notification) || { path: '/app?view=chat-overview', state: null };

    // Anstoss "Wieder treffen?" (Issue #668): in den Chat, mit der Herkunft fuer
    // "Session vorschlagen" (actionUrl /chat/<id>?origin=REJOIN_NUDGE).
    case 'SESSION_REJOIN_NUDGE':
      if (typeof actionUrl === 'string' && actionUrl.startsWith('/chat/')) {
        return { path: actionUrl, state: null };
      }
      return chatTarget(notification) || { path: '/app?view=chat-overview', state: null };

    case 'CONNECTION_DECLINED':
      return { path: '/app?view=matches', state: null };

    case 'SESSION_INVITED':
    case 'NEW_PUBLIC_SESSION':
    case 'SESSION_START':
    case 'SESSION_COMPLETED':
    // Host tippt darauf und landet in der Session, wo die Liste "Entfernt"
    // mit dem Knopf "Wieder zulassen" steht.
    case 'SESSION_READMIT_REQUEST':
      return sessionTarget(notification) || { path: '/sessions', state: null };

    // Abgesagte Session (Okt 2026, Review sessions-4): Die Session selbst ist
    // tot, die Detailseite leitete ohnehin nach /sessions weiter.
    case 'SESSION_CANCELLED':
      return { path: '/sessions', state: null };

    case 'SKILL_REQUEST':
      return { path: '/dashboard/admin/skills', state: null };

    case 'SKILL_REQUEST_APPROVED':
    case 'SKILL_REQUEST_REJECTED':
      return { path: '/profile/edit', state: null };

    // Level-Up + Badge: die Aura-Seite. Bis September 2026 stand hier
    // '/app?view=aura' - einen View "aura" kannte MainScreen nie, der Tipp
    // landete auf der Startseite.
    case 'AURA_UPDATE':
    case 'BADGE_EARNED':
      return { path: '/aura-stats', state: null };

    case 'WELCOME':
      return { path: '/welcome', state: null };

    default:
      if (actionUrl && typeof actionUrl === 'string' && actionUrl.startsWith('/')) {
        return { path: actionUrl, state: null };
      }
      return null;
  }
};

/**
 * Führt den Klick auf eine Notification aus: markiert als gelesen,
 * navigiert zum aufgelösten Ziel oder zeigt einen Toast, wenn die
 * Benachrichtigung nicht mehr gültig ist.
 *
 * @param {Function} navigate - react-router navigate
 * @param {Object} notification - Notification-Objekt
 * @param {Object} opts
 * @param {Function} [opts.markAsRead] - (id) => Promise
 * @param {Function} [opts.onBeforeNavigate] - Callback vor navigate (z.B. Panel schliessen)
 */
export const navigateFromNotification = async (navigate, notification, opts = {}) => {
  const { markAsRead, onBeforeNavigate } = opts;

  if (notification?.status === 'UNREAD' && markAsRead) {
    try { await markAsRead(notification.id); } catch { /* silent */ }
  }

  const target = resolveNotificationTarget(notification);
  if (!target) {
    toast.info('Diese Benachrichtigung ist nicht mehr gültig.', { autoClose: 3000 });
    return false;
  }

  if (onBeforeNavigate) {
    try { onBeforeNavigate(); } catch { /* silent */ }
  }

  navigate(target.path, target.state ? { state: target.state } : undefined);
  return true;
};
